import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Typography,
  Spin,
  Button,
  Space,
  Row,
  Col,
  Modal,
  Form,
  Input,
  Select,
  message,
} from "antd";
import {
  ReloadOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";
import {
  getDeliveryIssues,
  approveIssue,
  rejectIssue,
  bulkProcessIssues,
} from "../services/DeliveryIssues";
import "./DeliveryIssues.css";
import {
  ISSUE_TYPE_CONFIG,
  ISSUE_PARTNER_LABELS,
  PARTNER_COLORS,
} from "../helpers/Constants";
import { formatINR } from "../helpers/Utils";
import { Chip } from "../components/delivery/Atoms";
import { IssueFilters } from "../components/delivery/IssueFilters";

const { Text } = Typography;

const DEFAULT_FILTERS = { deliveryPartner: [], lspName: "", dateRange: null };

const ISSUE_STATUS = {
  in_progress: {
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FDE68A",
    label: "In Progress",
  },
  approved: {
    color: "#059669",
    bg: "#ECFDF5",
    border: "#A7F3D0",
    label: "Approved",
  },
  rejected: {
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    label: "Rejected",
  },
  processed: {
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    label: "Processed",
  },
};

function getIssueTypeConfig(issueType) {
  const cfg = ISSUE_TYPE_CONFIG[issueType];
  if (!cfg) return null;
  return { label: cfg.label, className: "issue-type-badge" };
}

function IssuePill({ status }) {
  const cfg = ISSUE_STATUS[status] ?? {
    color: "#6B7280",
    bg: "#F9FAFB",
    border: "#E5E7EB",
    label: status,
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: 20,
        padding: "3px 10px",
        color: cfg.color,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
}

function fmt(ts) {
  if (!ts) return <Text type="secondary">—</Text>;
  const d = new Date(ts);
  const formatted = d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return <span style={{ fontSize: 13, color: "#334155" }}>{formatted}</span>;
}

export default function DeliveryIssues() {
  const [issues, setIssues] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursor, setPreviousCursor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  // pageSize is learned from the first full page so "Showing X – Y" stays accurate.
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectForm] = Form.useForm();

  // ── Search + filters ──────────────────────────────────────────────────────
  const [searchType, setSearchType] = useState("order_id");
  const [searchInput, setSearchInput] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const debouncedCommitSearch = useMemo(
    () => debounce((val) => setCommittedSearch(val), 500),
    [],
  );
  useEffect(() => () => debouncedCommitSearch.cancel(), [debouncedCommitSearch]);

  const handleSearchInputChange = (rawValue) => {
    const value =
      searchType === "order_id" ? rawValue.replace(/\D/g, "") : rawValue;
    setSearchInput(value);
    debouncedCommitSearch(value);
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    debouncedCommitSearch.cancel();
    setSearchInput("");
    setCommittedSearch("");
  };

  const hasActiveSearchOrFilter =
    Boolean(committedSearch) ||
    filters.deliveryPartner.length > 0 ||
    Boolean(filters.lspName) ||
    Boolean(filters.dateRange);

  const handleClearFilters = () => {
    debouncedCommitSearch.cancel();
    setSearchType("order_id");
    setSearchInput("");
    setCommittedSearch("");
    setFilters(DEFAULT_FILTERS);
  };

  const buildQueryFilters = useCallback(() => {
    const params = {};
    if (searchType === "outlet_name") params.outlet_name = committedSearch;
    if (searchType === "order_id") params.order_id = committedSearch;
    if (filters.deliveryPartner.length) {
      params.delivery_partner = filters.deliveryPartner.join(",");
    }
    if (filters.lspName) params.lsp_name = filters.lspName;
    if (filters.dateRange) {
      const [start, end] = filters.dateRange;
      params.start_date = start.clone().startOf("day").toISOString();
      params.end_date = end.clone().endOf("day").toISOString();
    }
    return params;
  }, [searchType, committedSearch, filters]);

  /**
   * Central fetch helper.
   * @param {string|null} cursorUrl  Full cursor URL from previous/next, or null for first page.
   * @param {number}      nextPage   1-based page number to record after the fetch.
   */
  const loadIssues = useCallback(
    (cursorUrl = null, nextPage = 1) => {
      setLoading(true);
      getDeliveryIssues(cursorUrl, cursorUrl ? {} : buildQueryFilters())
        .then((data) => {
          const results = Array.isArray(data) ? data : (data.results ?? []);
          setIssues(results);
          setNextCursor(data.next ?? null);
          setPreviousCursor(data.previous ?? null);
          setCurrentPage(nextPage);
          // Pin the page size to the first full page so subsequent "Showing X – Y"
          // calculations are correct even on the final (possibly shorter) page.
          if (nextPage === 1) {
            setPageSize(results.length || 10);
          }
        })
        .finally(() => setLoading(false));
    },
    [buildQueryFilters],
  );

  // Any change to search or filters resets the cursor and reloads page 1.
  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const setAction = (id, val) =>
    setActionLoading((prev) => ({ ...prev, [id]: val }));

  const optimisticUpdate = (id, patch) =>
    setIssues((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    );

  const handleApprove = async (record) => {
    setAction(record.id, "approve");
    try {
      await approveIssue(record.id);
      optimisticUpdate(record.id, { status: "approved" });
      message.success("Issue approved");
    } finally {
      setAction(record.id, null);
    }
  };

  const handleRejectClick = (record) => {
    setSelectedIssue(record);
    setIsRejectModalVisible(true);
  };

  const handleRejectCancel = () => {
    rejectForm.resetFields();
    setIsRejectModalVisible(false);
    setSelectedIssue(null);
  };

  const handleRejectSubmit = async (values) => {
    setIsRejecting(true);
    setAction(selectedIssue.id, "reject");
    try {
      await rejectIssue(selectedIssue.id, {
        rejection_reason: values.rejection_reason.trim(),
      });
      optimisticUpdate(selectedIssue.id, { status: "rejected" });
      message.success("Issue rejected");
      rejectForm.resetFields();
      setIsRejectModalVisible(false);
      setSelectedIssue(null);
    } catch {
      message.error("Failed to reject issue");
    } finally {
      setIsRejecting(false);
      setAction(selectedIssue.id, null);
    }
  };

  const handleMarkProcessed = async (record) => {
    setAction(record.id, "process");
    try {
      await bulkProcessIssues([record.id]);
      optimisticUpdate(record.id, {
        status: "processed",
        processed_at: new Date().toISOString(),
      });
      message.success("Marked as processed");
    } finally {
      setAction(record.id, null);
    }
  };

  const handleBulkProcess = async () => {
    const approvedIds = selectedRowKeys.filter((id) => {
      const issue = issues.find((r) => r.id === id);
      return issue?.status === "approved";
    });
    if (!approvedIds.length) {
      message.warning("No approved issues selected");
      return;
    }
    setBulkLoading(true);
    try {
      await bulkProcessIssues(approvedIds);
      const now = new Date().toISOString();
      setIssues((prev) =>
        prev.map((r) =>
          approvedIds.includes(r.id)
            ? { ...r, status: "processed", processed_at: now }
            : r,
        ),
      );
      setSelectedRowKeys([]);
      message.success(`${approvedIds.length} issue(s) marked as processed`);
    } finally {
      setBulkLoading(false);
    }
  };

  const columns = [
    {
      title: "Order ID",
      dataIndex: ["placed_order", "id"],
      width: 130,
      render: (id) => (
        <Text style={{ fontWeight: 600, color: "#2563EB", fontSize: 13 }}>
          {id ?? "—"}
        </Text>
      ),
    },
    {
      title: "Outlet",
      dataIndex: "outlet_name",
      width: 160,
      render: (name) => (
        <Text style={{ fontSize: 13, color: "#1E293B" }}>{name ?? "—"}</Text>
      ),
    },
    {
      title: "Delivery Partner",
      dataIndex: ["delivery", "delivery_partner"],
      width: 140,
      render: (partner) => (
        <Chip
          label={ISSUE_PARTNER_LABELS[partner] || partner}
          color={PARTNER_COLORS[partner] || "#475569"}
        />
      ),
    },
    {
      title: "LSP",
      dataIndex: ["delivery", "lsp_name"],
      width: 140,
      render: (lspName) => <Chip label={lspName || "-"} />,
    },
    {
      title: "Order Amount",
      dataIndex: ["placed_order", "final_total"],
      width: 130,
      render: (amount) => {
        const formatted = formatINR(amount);
        return formatted ? (
          <Text style={{ fontWeight: 600, color: "#0F172A", fontSize: 13 }}>
            {formatted}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      width: 150,
      render: fmt,
    },
    {
      title: "Refund (₹)",
      dataIndex: "refund_amount",
      width: 110,
      render: (amt) =>
        amt != null ? (
          <Text style={{ fontWeight: 600, color: "#0F172A", fontSize: 13 }}>
            ₹{amt}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Comment",
      dataIndex: "issue_comment",
      width: 220,
      render: (c, record) => {
        const typeCfg = getIssueTypeConfig(record.issue_type);
        return (
          <div className="issue-comment-cell">
            {typeCfg && (
              <span className={typeCfg.className}>{typeCfg.label}</span>
            )}
            <Text
              style={{ fontSize: 13, color: "#475569", textWrap: "auto" }}
              ellipsis={{ tooltip: c }}
            >
              {c ?? "—"}
            </Text>
          </div>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      width: 130,
      render: (u) => (
        <Text style={{ fontSize: 13, color: "#334155" }}>{u ?? "—"}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      filters: Object.entries(ISSUE_STATUS).map(([val, cfg]) => ({
        text: cfg.label,
        value: val,
      })),
      onFilter: (value, record) => record.status === value,
      render: (status) => <IssuePill status={status} />,
    },
    {
      title: "Processed At",
      dataIndex: "processed_at",
      width: 150,
      render: fmt,
    },
    {
      title: "Actions",
      key: "actions",
      width: 210,
      fixed: "right",
      render: (_, record) => {
        const busy = actionLoading[record.id];
        if (record.status === "in_progress") {
          return (
            <Space size={6}>
              <Button
                size="small"
                type="primary"
                loading={busy === "approve"}
                disabled={!!busy}
                onClick={() => handleApprove(record)}
                style={{
                  borderRadius: 6,
                  background: "#059669",
                  borderColor: "#059669",
                  boxShadow: "none",
                  fontSize: 12,
                }}
              >
                Approve
              </Button>
              <Button
                size="small"
                danger
                loading={busy === "reject"}
                disabled={!!busy}
                onClick={() => handleRejectClick(record)}
                style={{ borderRadius: 6, boxShadow: "none", fontSize: 12 }}
              >
                Reject
              </Button>
            </Space>
          );
        }
        if (record.status === "approved") {
          return (
            <Button
              size="small"
              loading={busy === "process"}
              disabled={!!busy}
              onClick={() => handleMarkProcessed(record)}
              style={{
                borderRadius: 6,
                borderColor: "#BFDBFE",
                background: "#EFF6FF",
                color: "#2563EB",
                boxShadow: "none",
                fontSize: 12,
              }}
            >
              Mark Processed
            </Button>
          );
        }
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        );
      },
    },
  ];

  const approvedSelected = selectedRowKeys.filter((id) => {
    const issue = issues.find((r) => r.id === id);
    return issue?.status === "approved";
  });

  // Cursor pagination footer calculations
  const pageStart = (currentPage - 1) * pageSize + 1;
  const pageEnd = pageStart + issues.length - 1;
  const pageLabel =
    issues.length === 0 ? "No results" : `Showing ${pageStart} – ${pageEnd}`;

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <Text
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#0F172A",
              letterSpacing: "-0.3px",
              display: "block",
            }}
          >
            Delivery Issues
          </Text>
          <Text
            style={{
              fontSize: 12,
              color: "#64748B",
              marginTop: 3,
              display: "block",
            }}
          >
            Support queue · approve, reject, or mark issues as processed
          </Text>
        </div>

        <Row justify="space-between" align="middle" style={{ width: "100%" }}>
          <Col>
            <Space size={8} wrap>
              <Input.Group compact style={{ display: "flex" }}>
                <Select
                  value={searchType}
                  onChange={handleSearchTypeChange}
                  style={{ width: 140 }}
                >
                  <Select.Option value="order_id">Order ID</Select.Option>
                  <Select.Option value="outlet_name">
                    Outlet Name
                  </Select.Option>
                </Select>
                <Input
                  placeholder={
                    searchType === "order_id"
                      ? "Search order ID…"
                      : "Search outlet name…"
                  }
                  inputMode={searchType === "order_id" ? "numeric" : "text"}
                  pattern={searchType === "order_id" ? "[0-9]*" : undefined}
                  prefix={<SearchOutlined style={{ color: "#CBD5E1" }} />}
                  value={searchInput}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  allowClear
                  style={{
                    width: 220,
                    borderColor: "#E2E8F0",
                  }}
                />
              </Input.Group>
              {hasActiveSearchOrFilter && (
                <Button
                  icon={<CloseCircleOutlined />}
                  onClick={handleClearFilters}
                  style={{
                    borderRadius: 8,
                    borderColor: "#E2E8F0",
                    color: "#64748B",
                    boxShadow: "none",
                  }}
                >
                  Clear Filters
                </Button>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadIssues(null, 1)}
                loading={loading}
                style={{
                  borderRadius: 8,
                  borderColor: "#E2E8F0",
                  color: "#64748B",
                  boxShadow: "none",
                }}
              />
              {selectedRowKeys.length > 0 && (
                <Text style={{ fontSize: 13, color: "#64748B" }}>
                  {selectedRowKeys.length} selected
                  {approvedSelected.length !== selectedRowKeys.length &&
                    ` (${approvedSelected.length} approved)`}
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Space size={8}>
              <IssueFilters value={filters} onApply={setFilters} />
              {selectedRowKeys.length > 0 && (
                <Button
                  type="primary"
                  loading={bulkLoading}
                  disabled={approvedSelected.length === 0}
                  onClick={handleBulkProcess}
                  style={{
                    borderRadius: 8,
                    background: "#2563EB",
                    borderColor: "#2563EB",
                    boxShadow: "none",
                  }}
                >
                  Mark as Processed ({approvedSelected.length})
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 1px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Table
          className="delivery-table"
          rowKey="id"
          columns={columns}
          dataSource={issues}
          loading={{ spinning: loading, indicator: <Spin size="large" /> }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (record) => ({
              disabled: record.status !== "approved",
            }),
          }}
          scroll={{ x: 1300 }}
          pagination={false}
        />

        {/* Custom cursor-pagination footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            padding: "10px 16px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            size="small"
            icon={<LeftOutlined />}
            disabled={!previousCursor || loading}
            onClick={() => loadIssues(previousCursor, currentPage - 1)}
            style={{ borderRadius: 6, boxShadow: "none" }}
          ></Button>
          <Text style={{ fontSize: 13, color: "#64748B", userSelect: "none" }}>
            {pageLabel}
          </Text>
          <Button
            size="small"
            icon={<RightOutlined />}
            iconPosition="end"
            disabled={!nextCursor || loading}
            onClick={() => loadIssues(nextCursor, currentPage + 1)}
            style={{ borderRadius: 6, boxShadow: "none" }}
          ></Button>
        </div>
      </div>

      <Modal
        title="Reject Issue"
        visible={isRejectModalVisible}
        onCancel={handleRejectCancel}
        footer={null}
        destroyOnClose
      >
        <Form form={rejectForm} layout="vertical" onFinish={handleRejectSubmit}>
          <Form.Item
            name="rejection_reason"
            label="Rejection Reason"
            rules={[
              { required: true, message: "Required" },
              {
                validator: (_, value) =>
                  value && value.trim().length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("Required")),
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter rejection reason"
              showCount
              maxLength={500}
            />
          </Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={handleRejectCancel} disabled={isRejecting}>
              Cancel
            </Button>
            <Button
              danger
              type="primary"
              htmlType="submit"
              loading={isRejecting}
              disabled={isRejecting}
            >
              Confirm Reject
            </Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
